import { onBeforeUnmount, onMounted, ref } from 'vue'
import { useCategoriesStore } from '@/stores/category'
import { useTransactionsStore } from '@/stores/transaction'
import { usePlaidItemsStore } from '@/stores/plaidItem'
import { useProfilesStore } from '@/stores/profile'
import { getCurrentYearAndMonth } from '@/utils'

export function useBackgroundRefresh(thresholdMinutes: number = 5) {
  const backgroundStartTime = ref<number | null>(null)
  const isRefreshing = ref(false)
  const threshold = thresholdMinutes * 60 * 1000

  const categoriesStore = useCategoriesStore()
  const transactionsStore = useTransactionsStore()
  const plaidItemsStore = usePlaidItemsStore()
  const profilesStore = useProfilesStore()

  const refreshData = async () => {
    if (isRefreshing.value) return

    isRefreshing.value = true

    try {
      // Load from local storage first for immediate UI updates
      await Promise.all([
        categoriesStore.loadFromDeviceStorage(),
        transactionsStore.loadFromDeviceStorage(),
        plaidItemsStore.loadFromDeviceStorage(),
        profilesStore.loadFromDeviceStorage(),
      ])

      const refreshPromises = [
        categoriesStore.fetchCategories().catch((err) => {
          console.warn('Failed to refresh categories:', err)
        }),
        plaidItemsStore.getItems().catch((err) => {
          console.warn('Failed to refresh plaid items:', err)
        }),
        profilesStore.fetchProfiles().catch((err) => {
          console.warn('Failed to refresh profiles:', err)
        }),
      ]

      const currentYearMonth = getCurrentYearAndMonth()
      const [year, month] = currentYearMonth.split('-')
      refreshPromises.push(
        transactionsStore
          .getTransactionsForAllItems(parseInt(year), month, false)
          .catch((err) => {
            console.warn('Failed to refresh transactions:', err)
          }),
      )

      await Promise.allSettled(refreshPromises)
    } catch (error) {
      console.error('Background refresh failed:', error)
    } finally {
      isRefreshing.value = false
    }
  }

  const handleVisibilityChange = () => {
    if (document.visibilityState === 'hidden') {
      backgroundStartTime.value = Date.now()
    } else if (document.visibilityState === 'visible') {
      if (backgroundStartTime.value) {
        const backgroundDuration = Date.now() - backgroundStartTime.value
        if (backgroundDuration > threshold) {
          refreshData()
        }
        backgroundStartTime.value = null
      }
    }
  }

  const setup = () => {
    document.addEventListener('visibilitychange', handleVisibilityChange)
  }

  const cleanup = () => {
    document.removeEventListener('visibilitychange', handleVisibilityChange)
  }

  onMounted(setup)
  onBeforeUnmount(cleanup)

  return {
    isRefreshing,
    refreshData,
  }
}
